import {
	Dialog,
	FormattedText,
	PersonAvatar,
	Toolbar
} from "client/Components";
import { useDataInputDialog, useUserTimezone } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { LinkAudienceKind, LinkPermission } from "./common";
import { SharedLink } from "./SharedLink";
import styles from "./ViewSpecificPeopleDetailsDialog.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

type ViewSpecificPeopleDetailsDialogProps = {
	fileName: string;
	url: string;
	members: readonly string[];
	permission: LinkPermission;
	onClose: () => void;
};

const ViewSpecificPeopleDetailsDialog = (props: ViewSpecificPeopleDetailsDialogProps): ReactElement => {
	const {
		fileName,
		url,
		members,
		permission,
		onClose
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	const sortedMembers = useMemo(
		(): readonly string[] => {
			const result: string[] = [...members];
			result.sort();
			return result;
		},
		[members]
	);

	return (
		<Dialog trackingId="sp-share-link-specific-people-details-dialog">
			<Dialog.Header
				title={(
					<FormattedText
						text={localize("_ViewLinkFor")}
						thingsToReplace={[
							{ what: "{0}", filler: fileName }
						]}
					/>
				)}
				onClose={onClose}
			/>

			<Dialog.Body>
				<SharedLink
					audience={LinkAudienceKind.SpecificPeople}
					permission={permission}
					trackingId="link"
					url={url}
					timezone={userTimezone}
					buttonKind="secondary"
				/>
				<section className={styles.section}>
					<h4 className={styles.header}>
						{localize("_SpecifiedPeople")}
					</h4>

					<ul className={styles.list}>
						{sortedMembers.map((x, i): ReactElement => (
							<li key={i}>
								<Member displayName={x} />
							</li>
						))}
					</ul>
				</section>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ViewSpecificPeopleDetailsDialog.displayName = "ViewSpecificPeopleDetailsDialog";

type MemberProps = {
	displayName: string;
};

const Member = (props: MemberProps): ReactElement => {
	const {
		displayName
	} = props;

	const initials = useMemo(
		(): string => {
			if (!displayName) {
				return "";
			}

			return displayName.split(/\s+/)
				.map(w => w && w.charAt(0))
				.join("")
				.substring(0, 2);
		},
		[displayName]
	);

	return (
		<div className={styles.item}>
			<PersonAvatar
				initials={initials}
			/>

			{displayName}
		</div>
	);
};
Member.displayName = "Member";

export type ViewSpecificPeopleDetailsDialogInitialData = Omit<ViewSpecificPeopleDetailsDialogProps, "onClose">;

export function useViewSpecificPeopleDetailsDialog(): [
	element: ReactElement | null,
	open: (props: ViewSpecificPeopleDetailsDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<ViewSpecificPeopleDetailsDialogInitialData, void>(
		(props, onClose) => (
			<ViewSpecificPeopleDetailsDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: ViewSpecificPeopleDetailsDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}
